import { useAuth } from '../../../../hooks'
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import cn from '../../../../lib/utils/cn'
import { Button, Input } from '../../../ui'
import Loader from '../../../common/Loader'
import {
	CurrentSetData,
	PermissionObject,
} from '../../../../lib/api/types/permissionSet.interfaces'
import { usePermissionSet } from '../../../../hooks/api/permissionSetHooks'
import { useOrganizationPermissionSets } from '../../../../hooks/api/organizationHooks'
import { useUserPermissionSets } from '../../../../hooks/api/userHooks'
import { OrganizationPermissionSetsItem } from '../../../../lib/api/types/organization.interfaces'
import { DocumentData } from '../../../../lib/api/types/document.interfaces'
import { useSelectSet } from '../../../../hooks/api/permissionHooks'

type SelectedSetPermissionItem = {
	type: number
	level: number
} & Partial<PermissionObject>

interface ManagingSetsProps {
	organizationId: string
	currentSet?: CurrentSetData
	currentObject: DocumentData
	isVisible: boolean
	onCancel: () => void
	onRefetch: () => void
	isAdmin: boolean
}

const ManagingSets: FC<ManagingSetsProps> = ({
	organizationId,
	currentSet,
	currentObject,
	isVisible,
	onCancel,
	onRefetch,
	isAdmin,
}) => {
	const { isAuthenticated } = useAuth()
	const [selectedSet, setSelectedSet] =
		useState<OrganizationPermissionSetsItem | null>(currentSet ?? null)
	const [permissions, setPermissions] = useState<
		Record<string, SelectedSetPermissionItem[]>
	>({})

	const { watch, register } = useForm({
		defaultValues: {
			search: '',
		},
	})

	const { search } = watch()

	const { data: organizationSetsData, isLoading: isOrganizationSetLoading } = useOrganizationPermissionSets(
		{ id: organizationId},
		{
			enabled: Boolean(organizationId && isAuthenticated && isVisible && isAdmin
			)
		}
	)

	const { data: userSetsData, isLoading: isUserSetLoading } = useUserPermissionSets( { enabled: Boolean(!isAdmin && isAuthenticated) })

	const { data: selectedPermissionSetData, isLoading: isLoadingSelectedSet } =
		usePermissionSet({ id: selectedSet?.id || ''})

	const { mutateAsync: applySet, isPending: isApplyingSet } = useSelectSet()

	useEffect(() => {
		if (
			!selectedSet?.id ||
			permissions[selectedSet.id] ||
			selectedSet?.id !== selectedPermissionSetData?.data?.data?.id ||
			isOrganizationSetLoading ||
			isUserSetLoading ||
			isLoadingSelectedSet
		)
			return

		setPermissions((prev) => ({
			...prev,
			[selectedSet?.id]:
				selectedPermissionSetData?.data?.data?.permissions?.map(
					({ to, ...object }) => ({
						...object,
						name: to?.name || to?.email || 'Unnamed',
						id: to?.id,
					})
				),
		}))
	}, [selectedSet, selectedPermissionSetData])

	const displayedSets = useMemo(
		() =>
			(isAdmin ? organizationSetsData?.data?.entries : userSetsData?.data)?.filter(({ name }) =>
				name?.toLowerCase()?.includes(search?.trim()?.toLowerCase())
			) || [],
		[search, organizationSetsData, userSetsData]
	)

	const handleApplySet = async () => {
		if (!currentObject?.id) return

		applySet(
			{ setId: selectedSet?.id || null, id: currentObject?.id},
			{
				onSuccess: () => {
					onRefetch()
					onCancel()
				},
			}
		)
	}

	const allSetsData = isAdmin ? organizationSetsData : userSetsData;
	const isApiLoading = isLoadingSelectedSet || (isAdmin ? isOrganizationSetLoading : isUserSetLoading)

	return (
		<Fragment>
			<p className="font-bold text-lg mb-4 text-text-main dark:text-text-main-dark">
				Select set
			</p>
			<div className="flex items-center justify-center gap-3 w-full h-[275px] mb-5">
				<div
					className={cn(
						'h-full border rounded-lg border-stroke-dark dark:border-stroke-light-dark  overflow-hidden default-shadow',
						selectedSet ? 'w-[40%]' : 'w-full'
					)}
				>
					<Input
						{...register('search')}
						wrapperClassName="p-2 bg-background-light dark:bg-background-dark"
						inputClassName="h-9"
						placeholder="Name"
					/>
					<div className="h-4/5 overflow-y-auto relative pb-2">
						{displayedSets?.length
							? displayedSets?.map((set) => {
								const isSelected = set?.id === selectedSet?.id
								return (
									<div
										key={set?.id}
										className={cn(
											'pl-4 pr-2.5 h-10 flex items-center cursor-pointer hover:bg-accent-light dark:hover:bg-accent-hover/50 box-border',
											isSelected
												? 'border border-accent bg-accent-light dark:bg-accent'
												: 'border-none border-b border-b-stroke-dark'
										)}
										onClick={() => setSelectedSet(isSelected ? null : set)}
									>
										<span className="ellipsis text-sm text-text-main dark:text-text-main-dark bg-transparent">
											{set?.name}
										</span>
									</div>
								)
							})
							: !isApiLoading && !!allSetsData && <p>No sets</p>}
						{isApiLoading &&
							!!(currentSet?.id
								? displayedSets?.length === 1
								: !allSetsData) && (
								<Loader
									size={30}
									classNameWrapper="absolute inset-1/2 -translate-x-1/2 -translate-y-full"
								/>
							)}
					</div>
				</div>
				{!!selectedSet && (
					<div className="h-full w-3/5 rounded-lg border border-stroke-dark dark:border-stroke-light-dark overflow-hidden bg-background-light dark:bg-background-light-dark default-shadow">
						<p className="p-3 text-text-main dark:text-text-main-dark font-bold text-sm border-b border-stroke-dark dark:border-stroke-light-dark truncate">
							{selectedSet?.name}
						</p>
						<div className="relative h-full overflow-y-auto">
							{isApiLoading && !permissions?.[selectedSet?.id] ? (
								<Loader
									size={30}
									classNameWrapper="absolute inset-1/2 -translate-x-1/2 -translate-y-full"
								/>
							) : (
								<ul className="p-0">
									{permissions?.[selectedSet?.id]?.map(
										({ name, email, level, id }, index) => {
											return (
												<>
													<li
														key={`${index}-${id}`}
														className={cn(
															'h-8 px-3 flex items-center justify-between bg-transparent text-text-main dark:text-text-main-dark',
															index % 2 === 0 &&
															'bg-white dark:bg-background-dark text-text-main dark:text-text-main-dark'
														)}
													>
														<span className="text-sm">
															{name || email || 'Unnamed object'}
														</span>
														<>
															<span className={cn('text-xs font-bold')}>
																{level === 1
																	? 'Write'
																	: level === 2
																		? 'Delete'
																		: level === 3
																			? 'None'
																			: 'Read'}
															</span>
														</>
													</li>
												</>
											)
										}
									)}
								</ul>
							)}
						</div>
					</div>
				)}
			</div>
			<div className="flex items-center justify-end gap-2">
				<Button variant="tertiary" label="Cancel" onClick={onCancel} />

				<Button
					variant="primary"
					tooltip={
						selectedSet?.id === currentSet?.id
							? 'This set is already selected '
							: ''
					}
					label="Apply"
					onClick={handleApplySet}
					disabled={Boolean(isApiLoading)}
					loading={isApplyingSet}
				/>
			</div>
		</Fragment>
	)
}

export default ManagingSets
